import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { 
    Bold, 
    Italic,
    Underline as UnderlineIcon,
    Link as LinkIcon, 
    Heading1, 
    Heading2, 
    Heading3,
    List, 
    ListOrdered,
    Type,
    Quote,
    Minus,
    Table as TableIcon,
    Maximize2,
    CheckSquare,
    Highlighter,
    Eraser,
    Palette
} from 'lucide-react';
import tippy from 'tippy.js';
import { ReactRenderer } from '@tiptap/react';

import SlashCommand from './SlashCommand';
import CommandList from './CommandList';
import FocusModeOverlay from './FocusModeOverlay';

// --- CSS for ModernEditor (Glassmorphism & TipTap) ---
const EditorStyles = () => (
    <style>{`
        .modern-editor .ProseMirror {
            outline: none !important;
            min-height: 150px;
            padding: 2rem;
            font-size: 1rem;
            line-height: 1.6;
            color: #1e293b;
        }

        .modern-editor .ProseMirror p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            float: left;
            color: #94a3b8;
            pointer-events: none;
            height: 0;
            font-style: italic;
        }

        /* Selection styling */
        .modern-editor .ProseMirror ::selection {
            background-color: rgba(24, 33, 109, 0.15);
        }

        /* Bubble Menu Styling - Glassmorphism */
        .bubble-menu {
            display: flex;
            background: rgba(255, 255, 255, 0.85);
            backdrop-filter: blur(16px) saturate(180%);
            -webkit-backdrop-filter: blur(16px) saturate(180%);
            border: 1px solid rgba(226, 232, 240, 0.8);
            border-radius: 14px;
            padding: 0.35rem;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
            gap: 4px;
            z-index: 100000;
        }

        .bubble-menu button {
            background: transparent;
            border: none;
            border-radius: 10px;
            color: #475569;
            padding: 0.6rem;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .bubble-menu button:hover {
            background: #f1f5f9;
            color: #18216D;
        }

        .bubble-menu button.is-active {
            background: #18216D;
            color: white;
            box-shadow: 0 4px 12px rgba(24, 33, 109, 0.2);
        }

        /* Slash Command Menu */
        .slash-menu {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-left: 4px solid #18216D;
            border-radius: 12px;
            padding: 0.5rem;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            min-width: 280px;
            max-height: 400px;
            overflow-y: auto;
            z-index: 100000;
        }

        /* Task List Styling */
        .modern-editor ul[data-type="taskList"] {
            list-style: none;
            padding: 0;
        }
        .modern-editor ul[data-type="taskList"] li {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.5rem;
        }
        .modern-editor ul[data-type="taskList"] li > label {
            flex: 0 0 auto;
            user-select: none;
            margin-right: 0.75rem;
            margin-top: 0.25rem;
        }
        .modern-editor ul[data-type="taskList"] li > div {
            flex: 1 1 auto;
        }
        .modern-editor ul[data-type="taskList"] input[type="checkbox"] {
            cursor: pointer;
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 6px;
            border: 2px solid #cbd5e1;
            appearance: none;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
        }
        .modern-editor ul[data-type="taskList"] input[type="checkbox"]:checked {
            background-color: #18216D;
            border-color: #18216D;
        }
        .modern-editor ul[data-type="taskList"] input[type="checkbox"]:checked::after {
            content: '✓';
            color: white;
            font-size: 0.8rem;
            font-weight: bold;
        }

        .slash-item {
            display: flex;
            align-items: center;
            gap: 14px;
            width: 100%;
            padding: 10px 14px;
            border-radius: 12px;
            text-align: left;
            transition: all 0.2s;
            color: #64748b;
        }

        .slash-item:hover, .slash-item.is-selected {
            background: #f8fafc;
            color: #18216D;
            transform: translateX(4px);
        }

        .slash-item .icon-box {
            width: 36px;
            height: 36px;
            background: #f8fafc;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #f1f5f9;
            transition: all 0.2s;
        }

        .slash-item.is-selected .icon-box {
            background: #EFF6FF;
            border-color: #BFDBFE;
            color: #18216D;
        }

        /* Table Styling */
        .modern-editor table {
            border-collapse: collapse;
            table-layout: fixed;
            width: 100%;
            margin: 1.5rem 0;
            overflow: hidden;
            border-radius: 16px;
            border: 1px solid #f1f5f9;
        }

        .modern-editor th,
        .modern-editor td {
            min-width: 1em;
            border: 1px solid #f1f5f9;
            padding: 12px 16px;
            vertical-align: top;
            box-sizing: border-box;
            position: relative;
        }

        .modern-editor table th {
            background-color: #f8fafc;
            font-weight: 800;
            text-align: left;
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.05em;
            color: #64748b;
        }

        /* Focus Mode Typography (Matching Student Note View) */
        .prose-enhanced .ProseMirror {
            outline: none !important;
            border: none !important;
        }
        .prose-enhanced .ProseMirror h1 { 
            font-size: 3rem; 
            font-weight: 900; 
            margin-bottom: 2rem; 
            color: #18216D; 
            letter-spacing: -0.05em;
            line-height: 1.1;
        }
        .prose-enhanced .ProseMirror h2 { 
            font-size: 2rem; 
            font-weight: 900; 
            margin-top: 3rem;
            margin-bottom: 1.5rem; 
            color: #18216D; 
            letter-spacing: -0.02em;
        }
        .prose-enhanced .ProseMirror h3 { 
            font-size: 1.5rem; 
            font-weight: 800; 
            margin-top: 2rem;
            margin-bottom: 1rem; 
            color: #18216D; 
        }
        .prose-enhanced .ProseMirror p { 
            font-size: 1.125rem; 
            line-height: 1.8; 
            color: #475569; 
            margin-bottom: 1.5rem;
            font-medium;
        }
        .prose-enhanced .ProseMirror li {
            font-size: 1.125rem;
            line-height: 1.8;
            color: #475569;
            margin-bottom: 0.5rem;
        }
        .prose-enhanced .ProseMirror blockquote { 
            border-left: 4px solid #6366f1; 
            background: #f5f3ff;
            padding: 1.5rem 2rem; 
            border-radius: 0 1rem 1rem 0;
            font-style: italic; 
            color: #4338ca; 
            margin: 2rem 0; 
        }
        .prose-enhanced .ProseMirror hr {
            border: none;
            border-top: 2px solid #f1f5f9;
            margin: 3rem 0;
        }

        /* Standard block styles fallback */
        .modern-editor .ProseMirror h1 { font-size: 1.875rem; font-weight: 800; margin-bottom: 1.5rem; color: #0f172a; }
        .modern-editor .ProseMirror h2 { font-size: 1.5rem; font-weight: 700; margin-bottom: 1rem; color: #1e293b; }
        .modern-editor .ProseMirror h3 { font-size: 1.25rem; font-weight: 600; margin-bottom: 0.75rem; color: #334155; }
        .modern-editor .ProseMirror ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
        .modern-editor .ProseMirror ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
        .modern-editor .ProseMirror blockquote { border-left: 4px solid #18216D; padding-left: 1.5rem; font-style: italic; color: #475569; margin: 1.5rem 0; }

        /* Custom Tippy Tooltip Styling */
        .tippy-box[data-theme~='kianda'] {
            background-color: #18216D;
            color: #fff;
            font-size: 10px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            padding: 2px 6px;
            border-radius: 8px;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .tippy-box[data-theme~='kianda'] .tippy-arrow {
            color: #18216D;
        }
    `}</style>
)

const ModernEditor = ({ value, onChange, placeholder = "Type '/' for commands...", compact = false, readOnly = false }) => {
    const [showFocusMode, setShowFocusMode] = React.useState(false);

    // Initialize tooltips once editor is ready
    useEffect(() => {
        tippy('[data-tippy-content]', {
            theme: 'kianda',
            animation: 'shift-away',
            duration: [200, 150],
            placement: 'top',
        });
    }, []);

    const editorExtensions = React.useMemo(() => {
        const baseExtensions = [
            StarterKit.configure({
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                heading: {
                    levels: [1, 2, 3],
                },
                // Disable duplicates that are imported separately
                blockquote: false,
                horizontalRule: false,
                codeBlock: false,
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-600 underline font-medium cursor-pointer',
                },
            }),
            Placeholder.configure({
                placeholder: placeholder,
            }),
            Image.configure({
                allowBase64: true,
                HTMLAttributes: {
                    class: 'rounded-[2rem] max-w-full h-auto my-8 shadow-2xl border border-slate-100',
                },
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            Highlight.configure({ multicolor: true }),
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
            TextStyle,
            Color,
        ];

        if (!compact) {
            baseExtensions.push(
                SlashCommand.configure({
                  suggestion: {
                    items: ({ query }) => {
                      return [
                        {
                            title: 'Text',
                            description: 'Just start writing with plain text.',
                            icon: <Type size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).setParagraph().run();
                            },
                        },
                        {
                          title: 'Heading 1',
                          description: 'Big selection heading.',
                          icon: <Heading1 size={18} />,
                          command: ({ editor, range }) => {
                            editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run();
                          },
                        },
                        {
                          title: 'Heading 2',
                          description: 'Medium section heading.',
                          icon: <Heading2 size={18} />,
                          command: ({ editor, range }) => {
                            editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run();
                          },
                        },
                        {
                          title: 'Heading 3',
                          description: 'Small section heading.',
                          icon: <Heading3 size={18} />,
                          command: ({ editor, range }) => {
                            editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run();
                          },
                        },
                        {
                          title: 'Bullet List',
                          description: 'Create a simple bulleted list.',
                          icon: <List size={18} />,
                          command: ({ editor, range }) => {
                            editor.chain().focus().deleteRange(range).toggleBulletList().run();
                          },
                        },
                        {
                          title: 'Numbered List',
                          description: 'Create a list with numbering.',
                          icon: <ListOrdered size={18} />,
                          command: ({ editor, range }) => {
                            editor.chain().focus().deleteRange(range).toggleOrderedList().run();
                          },
                        },
                        {
                            title: 'Task List',
                            description: 'Track benchmarks and goals.',
                            icon: <CheckSquare size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).toggleTaskList().run();
                            },
                        },
                        {
                            title: 'Highlight',
                            description: 'Mark important text in gold.',
                            icon: <Highlighter size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).setHighlight({ color: '#FEF08A' }).run();
                            },
                        },
                        {
                            title: 'Divider',
                            description: 'Visually divide sections.',
                            icon: <Minus size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).setHorizontalRule().run();
                            },
                        },
                        {
                            title: 'Table (2x2)',
                            description: 'Insert a compact 2x2 table.',
                            icon: <TableIcon size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run();
                            },
                        },
                        {
                            title: 'Table (3x3)',
                            description: 'Insert a standard 3x3 table.',
                            icon: <TableIcon size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
                            },
                        },
                        {
                            title: 'Table (4x4)',
                            description: 'Insert a large 4x4 data grid.',
                            icon: <TableIcon size={18} />,
                            command: ({ editor, range }) => {
                              editor.chain().focus().deleteRange(range).insertTable({ rows: 4, cols: 4, withHeaderRow: true }).run();
                            },
                        },
                      ].filter(item => item.title.toLowerCase().startsWith(query.toLowerCase()));
                    },
                    render: () => {
                      let component;
                      let popup;

                      return {
                        onStart: props => {
                          component = new ReactRenderer(CommandList, {
                            props,
                            editor: props.editor,
                          });

                          if (!props.clientRect) {
                            return;
                          }

                          popup = tippy('body', {
                            getReferenceClientRect: props.clientRect,
                            appendTo: () => document.body,
                            content: component.element,
                            showOnCreate: true,
                            interactive: true,
                            trigger: 'manual',
                            placement: 'bottom-start',
                          });
                        },
                        onUpdate: props => {
                          component.updateProps(props);

                          if (!props.clientRect) {
                            return;
                          }

                          popup[0].setProps({
                            getReferenceClientRect: props.clientRect,
                          });
                        },
                        onKeyDown: props => {
                          if (props.event.key === 'Escape') {
                            popup[0].hide();
                            return true;
                          }

                          return component.ref?.onKeyDown(props);
                        },
                        onExit: () => {
                          popup[0].destroy();
                          component.destroy();
                        },
                      };
                    },
                  },
                })
            );
        }

        return baseExtensions;
    }, [compact]);

    const editor = useEditor({
        extensions: editorExtensions,
        content: value || '',
        editable: !readOnly,
        onUpdate: ({ editor }) => {
            if (onChange) {
                onChange(editor.getHTML());
            }
        },
    });

    // Update editor content when 'value' prop changes externally
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);
        }
    }, [value, editor]);

    if (!editor) {
        return <div className="animate-pulse bg-slate-50 rounded-[2.5rem] h-[200px]" />;
    }

    return (
        <div className="modern-editor relative group transition-all duration-500 bg-white border border-slate-200 rounded-[2.5rem] hover:border-slate-300 hover:shadow-2xl hover:shadow-slate-200/40">
            <EditorStyles />
            
            {editor && !readOnly && (
                <BubbleMenu editor={editor} className="bubble-menu">
                    <button
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={editor.isActive('bold') ? 'is-active' : ''}
                        data-tippy-content="Bold (Ctrl+B)"
                    >
                        <Bold size={16} />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={editor.isActive('italic') ? 'is-active' : ''}
                        data-tippy-content="Italic (Ctrl+I)"
                    >
                        <Italic size={16} />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        className={editor.isActive('underline') ? 'is-active' : ''}
                        data-tippy-content="Underline (Ctrl+U)"
                    >
                        <UnderlineIcon size={16} />
                    </button>
                    <button
                        onClick={() => {
                            const url = window.prompt('URL');
                            if (url) editor.chain().focus().setLink({ href: url }).run();
                        }}
                        className={editor.isActive('link') ? 'is-active' : ''}
                        data-tippy-content="Link"
                    >
                        <LinkIcon size={16} />
                    </button>
                    <div className="w-[1px] h-4 bg-slate-200 mx-1 self-center" />
                    <button
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                        data-tippy-content="Heading 1"
                    >
                        <Heading1 size={16} />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                        data-tippy-content="Heading 2"
                    >
                        <Heading2 size={16} />
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                        data-tippy-content="Heading 3"
                    >
                        <Heading3 size={16} />
                    </button>
                    <div className="w-[1px] h-4 bg-slate-200 mx-1 self-center" />
                    
                    {/* Highlight Palette */}
                    <div className="flex items-center gap-1 px-1">
                        {[
                            { color: '#FEF08A', label: 'Gold' },
                            { color: '#BBF7D0', label: 'Emerald' },
                            { color: '#BFDBFE', label: 'Sky' },
                            { color: '#FECACA', label: 'Rose' }
                        ].map((h) => (
                            <button
                                key={h.color}
                                onClick={() => editor.chain().focus().setHighlight({ color: h.color }).run()}
                                className="w-5 h-5 rounded-md border border-slate-200 transition-transform hover:scale-110 active:scale-95"
                                style={{ backgroundColor: h.color }}
                                data-tippy-content={`Highlight ${h.label}`}
                            />
                        ))}
                    </div>

                    <div className="w-[1px] h-4 bg-slate-200 mx-1 self-center" />

                    <button
                        onClick={() => editor.chain().focus().unsetHighlight().run()}
                        className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-red-500 transition-colors"
                        title="Remove Highlight (Select text then click)"
                        data-tippy-content="Remove Highlight (Select text then click)"
                    >
                        <Eraser size={16} />
                    </button>
                    
                    <button
                        onClick={() => editor.chain().focus().setColor('#18216D').run()}
                        data-tippy-content="Kianda Navy Text"
                    >
                        <Palette size={16} className="text-[#18216D]" />
                    </button>

                    <div className="w-[1px] h-4 bg-slate-200 mx-1 self-center" />

                    <button
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={editor.isActive('bulletList') ? 'is-active' : ''}
                        data-tippy-content="Bullet List"
                    >
                        <List size={16} />
                    </button>
                </BubbleMenu>
            )}

            {/* Content Area - Unmount when in Focus Mode to prevent state conflicts */}
            {!showFocusMode && <EditorContent editor={editor} className="p-2" />}
            
            {/* Focus Mode Trigger Overlay - Hidden if compact mode */}
            {!compact && (
                <div className="absolute right-8 bottom-6 flex items-center gap-4 transition-opacity opacity-0 group-hover:opacity-100">
                    <span className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em] bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100/50">
                        / for commands
                    </span>
                    
                    <button
                        onClick={() => setShowFocusMode(true)}
                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#18216D] hover:border-indigo-100 hover:shadow-lg transition-all active:scale-95 flex items-center gap-2 pointer-events-auto shadow-sm"
                        title="Enter Focus Mode"
                    >
                        <Maximize2 size={16} />
                        <span className="text-[9px] font-black uppercase tracking-widest pr-1">Focus Mode</span>
                    </button>
                </div>
            )}

            {/* Independent Focus Mode Overlay (Portal) */}
            {showFocusMode && (
                <FocusModeOverlay 
                    editor={editor} 
                    onClose={() => setShowFocusMode(false)} 
                />
            )}
        </div>
    );
};

export default ModernEditor;
